import { useEffect, useState, useMemo } from 'react'
import {
  X, LoaderCircle, Pencil, FileDown,
  ChevronDown, ChevronUp
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { getTestDetail, getSubjectName, updateCourseTestApproval } from '@/api/kuizy'
import {
  formatDateTime, typeExam_Order, typeOption_Order,
  levelMapping, optionLabel
} from '@/settings/master'
import TestExportModal from './TestExportModal'
import { EXAM_STATUS } from '../settings/master'

export default function ExamDetailModal({ visible, testId, onClose, cid, ctId }) {
  const nav = useNavigate()

  const [loading, setLoading] = useState(false)
  const [detail, setDetail] = useState(null)
  const [err, setErr] = useState(null)
  const [exportOpen, setExportOpen] = useState(false)
  const [contentName, setContentName] = useState('')
  const [expanded, setExpanded] = useState(new Set())

  const statusInfo = EXAM_STATUS[detail?.approval_status] || 0

  useEffect(() => {
    if (!cid) return
    getSubjectName(cid)
      .then(r => setContentName(r.data?.name || cid))
      .catch(() => setContentName(cid))
  }, [cid])

  const fetchDetail = async () => {
    if (!testId) return

    try {
      setLoading(true)
      setErr(null)

      const r = await getTestDetail(
        testId,
        localStorage.getItem('orgId'),
        ctId
      )

      setDetail(r.data)
    } catch (e) {
      setErr(e.message || 'Load error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!visible || !testId) return
    fetchDetail()
  }, [visible, testId])


  const toggle = id => {
    const s = new Set(expanded)
    s.has(id) ? s.delete(id) : s.add(id)
    setExpanded(s)
  }

  const optionStyle = useMemo(() => detail?.option_style || 'thai', [detail])

  const handleAction = async (id, status) => {
    try {
      await updateCourseTestApproval(id, status)
      await fetchDetail() // 👈 refresh หลัง update สำเร็จ

      window.dispatchEvent(new Event("approval-updated"))
    } catch (err) {
      console.error(err)
    }
  }


  if (!visible) return null

  return (
    <AnimatePresence>
      <motion.div key="bg" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/55 backdrop-blur-sm z-[1100] overflow-y-auto flex items-start sm:items-center justify-center p-4">
        <motion.div key="card" initial={{ y: 32, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          exit={{ y: 32, opacity: 0 }} transition={{ duration: .25, ease: 'easeOut' }}
          className="w-full h-full sm:w-[95%]  max-h-screen sm:h-[90vh] bg-white rounded-xl shadow-xl flex flex-col overflow-hidden">

          <header className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-info">
            <h2 className="text-base sm:text-lg font-semibold line-clamp-1">
              {detail?.name || 'รายละเอียดชุดข้อสอบ'}
            </h2>
            <button onClick={onClose} className="text-gray-600 hover:text-primary shrink-0">
              <X size={20} />
            </button>
          </header>

          <main className="flex-1 overflow-y-auto p-6">
            {loading && (
              <div className="flex items-center justify-center py-20">
                <LoaderCircle className="animate-spin" size={34} />
              </div>
            )}

            {err && !loading && <div className="text-center text-red-600 py-16">{err}</div>}

            {!loading && detail && (
              <>
                <div className="flex flex-wrap gap-3 items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      {/* สถานะการเปิด/ปิด */}
                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${detail.active ? 'bg-success text-white' : 'bg-red-600 text-white'}`}>
                        {detail.active ? 'ACTIVE' : 'CLOSED'}
                      </span>

                      {/* สถานะการอนุมัติ */}
                      <span
                        className={`text-xs px-2 py-0.5 rounded border font-medium ${statusInfo.style}`}
                      >
                        {statusInfo.label}
                      </span>

                      <span className="text-sm font-medium text-gray-500">
                        ({detail.questions?.length ?? 0} ข้อ)
                      </span>
                    </div>

                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    <button
                      onClick={() => nav(`/content/${detail.content_id}/tests/${detail.id}/edit`)}
                      className="inline-flex justify-center items-center gap-1 bg-primary hover:bg-secondary text-white text-xs sm:text-sm rounded-lg px-3 py-2 w-full sm:w-auto">
                      <Pencil size={14} /> แก้ไข
                    </button>

                    <button
                      onClick={() => setExportOpen(true)}
                      className="inline-flex justify-center items-center gap-1 bg-secondary hover:bg-primary text-white text-xs sm:text-sm rounded-lg px-3 py-2 w-full sm:w-auto">
                      <FileDown size={14} /> Export
                    </button>

                    {/* แสดงเฉพาะตอนรออนุมัติ */}
                    {detail.approval_status === 0 && (
                      <>
                        <button
                          onClick={() => handleAction(detail.course_test_id, 1)}
                          className="inline-flex justify-center items-center gap-1 bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm rounded-lg px-3 py-2 w-full sm:w-auto">
                          อนุมัติ
                        </button>

                        <button
                          onClick={() => handleAction(detail.course_test_id, 2)}
                          className="inline-flex justify-center items-center gap-1 bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm rounded-lg px-3 py-2 w-full sm:w-auto">
                          ไม่อนุมัติ
                        </button>
                      </>
                    )}
                  </div>

                </div>

                <section className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 text-sm mb-6">
                  <div className="space-y-1">
                    <p><b>เนื้อหา:</b> {contentName}</p>
                    <p><b>เรียงข้อสอบ:</b> {typeExam_Order[detail.exam_order] || 'normal'}</p>
                    <p><b>เรียงตัวเลือก:</b> {typeOption_Order[detail.option_order] || 'normal'}</p>
                    <p><b>เกณฑ์ผ่าน:</b> {detail.criteria}%</p>
                  </div>
                  <div className="space-y-1">
                    <p><b>วันที่เริ่ม:</b> {detail.start_time ? formatDateTime(detail.start_time) : '-'}</p>
                    <p><b>วันที่สิ้นสุด:</b> {detail.end_time ? formatDateTime(detail.end_time) : '-'}</p>
                    <p><b>สร้างเมื่อ:</b> {formatDateTime(detail.created_at)}</p>
                  </div>
                </section>

                {detail.description && (
                  <section className="bg-info/50 rounded-lg p-4 mb-6">
                    <h4 className="font-semibold mb-1 text-sm">รายละเอียด</h4>
                    <p className="text-sm whitespace-pre-line">{detail.description}</p>
                  </section>
                )}

                <section>
                  <h4 className="font-semibold mb-3 text-sm">รายการคำถาม ({detail.questions.length})</h4>
                  <div className="divide-y border rounded-lg">
                    {detail.questions.map((q, idx) => {
                      const ex = expanded.has(q.id)
                      return (
                        <div key={q.id} className="p-4">
                          <div className="flex items-start gap-2">
                            <button onClick={() => toggle(q.id)} className="text-primary mt-[2px]">
                              {ex ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                            <p className="font-medium flex-1">{idx + 1}. {q.question}</p>
                            <span className="text-xs text-gray-500">{levelMapping[q.level] || ''}</span>
                          </div>
                          {ex && (
                            <ul className="mt-3 ml-7 list-disc text-sm space-y-1">
                              {q.answer.map((a, i) => {
                                const k = Object.keys(a)[0]
                                const v = a[k]
                                const desc = typeof v === 'object' ? v.description : v
                                const corr = Array.isArray(q.correct) ? q.correct.includes(k)
                                  : (q.multi_correct ? q.correct?.includes(k) : q.correct === k)
                                return (
                                  <li key={k} className={corr ? 'text-green-700' : ''}>
                                    {optionLabel(i, optionStyle)}. {desc}
                                  </li>
                                )
                              })}
                            </ul>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </section>
              </>
            )}
          </main>

          <TestExportModal visible={exportOpen} cid={cid} testId={testId} onClose={() => setExportOpen(false)} />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
